/**
 * Talking to the API.
 *
 * One place, so the token is attached once and a 401 means one thing
 * everywhere: the session lapsed, and the app returns to login rather than
 * rendering an error a pharmacist cannot act on.
 */

import type { CheckCell } from './findingsList.ts'
import {
  feedbackQuery,
  type FeedbackReviewFilters,
  type FeedbackSequence,
} from './feedbackReviewSession.ts'
import type { FindingState } from './findingsReport.ts'
import type { RuleOutcomeRecord } from './ruleOutcomes.ts'
import type { ErpCodesCheckResult } from './erpCodes.ts'
import type { CopiedFrom, SimulationCopyBasis, SimulationCopyMade } from './simulationCopy.ts'
import type {
  AdversarialRun,
  ReviewAssessment,
  ReviewAssessmentRecord,
} from './adversarialReview.ts'

const TOKEN_KEY = 'rp.session'

export function storedToken(): string | undefined {
  try {
    return window.localStorage.getItem(TOKEN_KEY) ?? undefined
  } catch {
    // A browser with site data undecided is not a broken app; it is a session
    // that will not survive a reload.
    return undefined
  }
}

export function storeToken(token: string | undefined): void {
  try {
    if (token === undefined) window.localStorage.removeItem(TOKEN_KEY)
    else window.localStorage.setItem(TOKEN_KEY, token)
  } catch {
    // Ignored deliberately — see above.
  }
}

export class SessionExpired extends Error {
  constructor() {
    /*
     * Empty on purpose.
     *
     * Screens render `cause.message` in an error banner, and an expired
     * session was arriving there as the words "a sessão expirou" over the
     * queue somebody was reading -- a sentence about the app's plumbing, in a
     * place reserved for things wrong with an verification.
     *
     * There is nothing for a person to do with it, so it says nothing and the
     * app takes them to the login page instead. Anything catching this can
     * still recognise it by name.
     */
    super('')
    this.name = 'SessionExpired'
  }
}

/**
 * A request the server refused, with what it said.
 *
 * Still an `Error` whose message is the server's sentence, which is all most
 * screens show. The status and body are for the few answers that carry more
 * than a sentence -- a 409 listing the filiais a number exists at (#465).
 */
export class RequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: Readonly<Record<string, unknown>>,
  ) {
    super(message)
    this.name = 'RequestError'
  }
}

/**
 * Fired when the server stops accepting this session.
 *
 * An event rather than a redirect from here, because this file makes requests
 * and knows nothing about routing -- and because a hard `location` change
 * would throw away whatever the person had typed on the way to finding out.
 */
export const SESSION_EXPIRED_EVENT = 'rp:session-expired'

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = storedToken()
  const response = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token === undefined ? {} : { Authorization: `Bearer ${token}` }),
      ...init.headers,
    },
  })

  if (response.status === 401) {
    storeToken(undefined)
    // Announced before it is thrown, so the app has already begun moving to
    // the login page by the time a caller's catch block runs.
    try {
      window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT))
    } catch {
      // No window in a test. The throw below is the whole contract there.
    }
    throw new SessionExpired()
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string }
    throw new RequestError(
      body.error ?? `a requisição falhou (${response.status})`,
      response.status,
      body,
    )
  }

  return (await response.json()) as T
}

/**
 * The two golden sets, as the API names them.
 *
 * `reader` measures whether we read the receita right; `resolution` whether we
 * pair what was read with what was keyed and say the right thing about it.
 * They are separate sets because they are separate questions, and a case can
 * be worth having in one and useless in the other.
 */
export type GoldenSetName = 'reader' | 'resolution'

/**
 * Where a document came from, as `AttachmentSource` in @rp/core names it.
 *
 * `erp` is the pharmacy's own record and is fixed; `manual` was attached on
 * the conferência by a person, and may be removed there (#276).
 */
export type AttachmentSource = 'erp' | 'manual'

/** One page of a receita, as the image listing describes it. */
export interface PageRef {
  readonly id: string
  readonly page: number
  readonly pageIndex: number
  readonly contentType: string
  readonly source: AttachmentSource
}

/** A receita somebody attached on the conferência. */
export interface ManualAttachment {
  /** `manual:<id>`: the attachment id the pages are listed under. */
  readonly id: string
  readonly filename: string
  readonly contentType: string
  readonly byteSize: number
  readonly uploadedBy: string
  readonly createdAt: string
}
/** `skipped` means it was not asked for; `exists` that it was already there. */
export type GoldenSetOutcome = 'created' | 'exists' | 'skipped'

export type TriageStatus = 'pending' | 'agree' | 'disagree' | 'all'

/**
 * What the conferente claimed about our output (#405), as `claimOf` in
 * `@rp/core` reads it: that a flagged divergence was not one, that we left a
 * real one quiet, or neither.
 */
export type FeedbackClaim = 'flagged-wrongly' | 'missed' | 'other'

/**
 * One piece of feedback, and how we judged it.
 *
 * A committed trail event: a comment, or a decision that disagreed with us or
 * said we missed something. `reason` on a decision is the pharmacist's note;
 * the reason they picked is `detail.reasonCode`, carried since #405.
 */
export interface FeedbackItem {
  id: string
  at: string
  kind: 'decision' | 'feedback'
  /** The finding it answers. Null on a comment. */
  fingerprint: string | null
  actor_email: string
  decision: string | null
  reason: string | null
  misread: boolean
  missed: boolean
  notes: string | null
  detail: {
    check?: string
    message?: string
    reasonCode?: string
    evidence?: Record<string, unknown>
  } | null
  verification_id: string
  branch_code: number
  quotation_number: number
  account_id: string
  account_name: string
  verdict: string | null
  triage_note: string | null
  triaged_by: string | null
  triaged_at: string | null
  claim: FeedbackClaim
  ruleTitle?: string
}

/** Mirrors `TriageGroup` in `@rp/core`. */
export interface FeedbackPanelGroup {
  check: string | null
  rule: string | null
  ruleTitle?: string
  classified: number
  agreed: number
  disagreed: number
  falsePositives: number
  falseNegatives: number
  aiRight: number
}

export interface FeedbackPanel {
  groups: FeedbackPanelGroup[]
  total: FeedbackPanelGroup
  /** Feedback in the window nobody has classified yet. */
  pending: number
}

/**
 * One thing a person did to a conferência.
 *
 * `detail` carries what the difference *was* when a decision was given --
 * snapshotted at the time, because `findings` is rewritten on every reverify
 * and a trail that resolved through it would decay into decisions with nothing
 * beside them.
 */
/**
 * One status the verification entered, from its history. `stage` and `reason`
 * are recorded since #489; older entries have neither.
 */
export interface StatusEntry {
  status: string
  at: string
  stage?: string
  reason?: string
}

export interface ConferenceEvent {
  at: string
  actor_email: string
  kind:
    | 'decision'
    | 'sent_for_correction'
    | 'reverification_requested'
    | 'reverified'
    | 'verified'
    | 'assigned'
    | 'unassigned'
    | 'feedback'
  fingerprint: string | null
  decision: string | null
  reason: string | null
  misread: boolean
  /** The text, in the actor's own words. What a `feedback` event carries. */
  notes: string | null
  /** Where the requisition was when it happened (#489); null before that was recorded. */
  stage?: string | null
  detail: {
    /** Groups a batch: every decision written by one conclusion shares it. */
    conclusionId?: string
    check?: string
    scope?: string
    message?: string
    /** The finding's own evidence, kept whole so the screen can read its sides. */
    evidence?: Record<string, unknown>
    outcome?: string
    decisions?: number
  } | null
}

export interface QueueRow {
  id: string
  branch_code: number
  /** The filial's name, falling back to its code when never synced. */
  branch_name?: string
  quotation_number: number
  status: string
  /** Why it stopped, shown on the badge. Absent on everything still moving. */
  failure_reason?: string | null
  /** What the pharmacy keyed, falling back to what we read. Null on neither. */
  patient_name: string
  /** How many different patients the keyed preparations name (#342); 0 or 1 usually. */
  patient_count?: number
  /** Who is working on it, already filtered by the lease. */
  assigned_to?: string | null
  /** The ERP's bench position. 0 means it has not moved, not that it is missing. */
  stage_pos?: number | null
  /** Decisions given on findings that needed one — the numerator of "3/5". */
  flagged_reviewed?: number | null
  /** The checkpoint the analysis reached. Null before it starts. */
  step: string | null
  /** A few of what is wrong, so the queue can say without being opened. */
  /**
   * What is wrong, in a few words, without opening the conferência -- each with
   * the disposition that says which kind of wrong it is.
   */
  previews: Array<{ text: string; disposition: string }>
  /** Only ever true for a super admin: the API filters simulations out for everyone else. */
  simulation: boolean
  updated_at: string
  /** When the pharmacy keyed it, by the ERP's clock. Null until discovery carries it. */
  keyed_at?: string | null
  /** The earliest pickup the pharmacy promised. What the bench is worked by. */
  pickup_at?: string | null
  divergence: number
  undecided: number
  ok: number
  info: number
  reviewed: number
  /** Back from a correction and waiting on its final check (#439). */
  returned_from_correction?: boolean
}

/** What each filter would show, so a chip can carry its own count. */
export interface QueueCounts {
  all: number
  flagged: number
  analysing: number
  clean: number
  /** Waiting on a correction, for the corrections list's tab (#439). */
  corrections?: number
}

export interface Finding {
  check: string
  scope: string
  disposition: 'ok' | 'info' | 'divergence' | 'undecided'
  score: number | null
  evidence: Record<string, unknown>
  regions: string[]
  fingerprint: string
  message: string | null
}

export interface Block {
  block_ref: string
  page: number
  text: string
  x: string
  y: string
  w: string
  h: string
}

export interface Review {
  fingerprint: string
  decision: string
  misread?: boolean
  verifier_email: string
  notes: string | null
  created_at: string
}

/** One row of an verification's timeline: a checkpoint began, or a status closed it. */
export interface TimelineEntry {
  step?: string | null
  status?: string | null
  at: string
}

/**
 * The progress strip's whole world: what `GET /:id/progress` answers.
 *
 * Deliberately the same field names the strip already reads off the detail,
 * so a caller can hand either to it.
 */
export interface VerificationProgress {
  status: string
  step: string | null
  failureReason: string | null
  timeline: unknown
  stagePos: number
  updatedAt: string
}

/**
 * A verification analysed before the evaluation changed (#605).
 *
 * The server decides it and sends what changed; nothing here compares revisions.
 */
export interface OutdatedEvaluation {
  ranUnder: number
  current: number
  /** What changed since it was analysed, oldest first, as written for the pharmacist. */
  changes: string[]
}

export interface VerificationDetail {
  verification: {
    id: string
    /** The ERP's bench position. 0 means it has not moved, not that it is missing. */
    stage_pos?: number | null
    branch_code: number
    quotation_number: number
    status: string
    /** The checkpoint the analysis reached, surviving the workflow itself. */
    step?: string
    /** Why it stopped, when it stopped badly. */
    failure_reason?: string | null
    /** Every checkpoint with when it began. See `stepDurations`. */
    timeline?: TimelineEntry[]
    prescription: unknown
    quotation: unknown
    /** Only ever true for a super admin. */
    simulation?: boolean
  }
  /** The conferência this simulation was copied from, when it was (#586). */
  copiedFrom?: CopiedFrom
  /** Set only while this awaits a pharmacist and ran before a change to the evaluation (#605). */
  outdatedEvaluation?: OutdatedEvaluation
  findings: Finding[]
  decisions: Review[]
  blocks: Block[]
  /*
   * What the workflow says about itself, when one is running.
   *
   * `status` was missing here while the wire had carried it all along, so the
   * screen could only ever read the row -- which is behind by exactly the
   * moment a person is watching.
   */
  live?: { status?: string; step?: string; outstanding?: number }
  /** Present only for an verification dispatched from a golden-set case. */
  goldenCase?: GoldenCaseSource
  /**
   * The selected products the catalogue actually holds.
   *
   * A name links to its product page only when its code is here. A code we
   * have never synced -- or a simulation, whose ingredients resolve to
   * nothing -- would otherwise send somebody to a not-found.
   */
  knownProducts?: string[]
  /** The product each keyed ingredient was keyed with, by ingredient id. */
  keyedCodes?: Record<string, string>
  /**
   * What each operational rule did when this was scored (#497). Absent for a
   * conferência scored before that was recorded, which is not an empty list.
   */
  ruleOutcomes?: RuleOutcomeRecord[]
  /**
   * Comments written on this pass and not yet carried into the histórico.
   *
   * Provisional, like the decisions: Concluir writes them into the trail. Sent
   * with the detail rather than fetched when the panel opens, because the icon
   * has to say how many are waiting before anybody clicks it.
   */
  comments?: VerificationComment[]
}

/**
 * A comment waiting on a conferência.
 *
 * Provisional until Concluir, like a decision: the histórico is written once,
 * from how they finally stood.
 */
export interface VerificationComment {
  id: string
  author_email: string
  body: string
  created_at: string
}

/** One product in our replica of the pharmacy's catalogue, with its terms. */
export interface Product {
  id: string
  erpCode: string
  name: string
  active: boolean
  syncedAt: string | null
  terms: ProductTerm[]
  /** What the catalogue knows about the substance. Null where it says nothing. */
  controlledList: string | null
  reportedToSngpc: boolean | null
  biopharmaceuticalClass: string | null
  hygroscopicity: number | null
  /**
   * This account's override of the ERP's type: treated as an excipient though
   * the ERP keys it as a component (ADR 0014). Null when the ERP's type stands.
   */
  excipientOverride: {
    markedBy: string
    markedAt: string
    /** The conferência it was set from; null when set on this page. */
    markedFrom: { id: string; branchCode: number | null; quotationNumber: number | null } | null
  } | null
  /** What this account says the product is the excipient for (#617); null when nothing. */
  excipientAnswers: ExcipientAnswers | null
  /** What the product is as a capsule shell, by its name and by the account's word (#636). */
  shell: ProductShell
}

export interface ProductTerm {
  id: string
  term: string
  kind: 'canonical' | 'synonym'
  source: 'erp' | 'model' | 'pharmacist'
  approved: boolean
  occurrences: number
  createdAt: string
}

/** One product retrieval offered for a term. */
export interface CatalogCandidate {
  erpCode: string
  productName: string
  matchedTerm: string
  kind: 'canonical' | 'synonym'
  source: 'erp' | 'model' | 'pharmacist'
  layer: 'exact' | 'synonym' | 'trigram'
  score: number
}

/** One golden-set case, as the index lists it. */
export interface GoldenCase {
  caseId: string
  writing: string
  status: string
  preparations: number
  ingredients: number
  /** How many labelled values carry a region. Zero means nothing to highlight. */
  boxed: number
  hasPage: boolean
}

/**
 * One resolution case, as the index lists it.
 *
 * The `expects` mix is the column that says whether the case is worth running:
 * a set of only `consistent` cases rewards a resolver that never speaks, which
 * is the failure mode the whole exercise exists to price.
 */
export interface ResolutionCaseSummary {
  caseId: string
  status: string
  notes: string
  products: number
  ingredients: number
  expects: { consistent: number; suggest_correction: number; unresolved: number }
}

export type ResolutionOutcome = 'consistent' | 'suggest_correction' | 'unresolved'

export interface ResolutionCaseIngredient {
  prescribed: string
  dose: string | null
  selected: string
  expect: ResolutionOutcome
  expectRival: string | null
  why: string
}

export interface ResolutionCaseFull {
  caseId: string
  catalogue: Array<{ erpCode: string; name: string; synonyms: string[] }>
  ingredients: ResolutionCaseIngredient[]
  status: string
  correctedBy: string | null
  correctedAt: string | null
  notes: string
}

export interface ObservedResolution {
  prescribed: string
  /** The code that was on trial. Half of what identifies a ingredient. */
  selected: string
  outcome: ResolutionOutcome
  rivalCode?: string
  reason?: string
  interpretation?: string
  uncertain?: boolean
  selectionScore?: number
  candidates?: Array<{
    erpCode: string
    productName: string
    layer: string
    matchedTerm: string
    score?: number
    why?: string
  }>
}

export interface ResolutionScore {
  rows: Array<{
    prescribed: string
    selected: string
    expected: ResolutionOutcome
    actual: ResolutionOutcome
    expectedRival: string | null
    actualRival?: string
    grade: 'correct' | 'false_accusation' | 'wrong_rival' | 'missed_catch' | 'other'
  }>
  total: number
  correct: number
  accuracy: number
  falseAccusations: number
  missedCatches: number
  wrongRivals: number
}

/** One catalogue product, as the products list shows it. */
export interface CatalogProduct {
  erpCode: string
  name: string
  active: boolean
  /** Approved terms that reach this product, including its own name. */
  terms: number
  /** Terms awaiting somebody's approval. */
  pending: number
  /** The ANVISA list, when the catalogue says it is on one. */
  controlledList?: string
  /** What the account says it is the excipient for (#617), when it said anything. */
  excipientAnswers?: string[]
  /** What it counts as when it is a capsule shell (#636); absent for any other product. */
  shellTraits?: string[]
}

/** What the status screen reads. */
/** One model, and whether anything is currently serving it. */
export interface ModelHealth {
  model: string
  ok: boolean
  /** Worst 30-minute uptime among the providers serving it. */
  uptime?: number
}

/**
 * A step's models, and what that adds up to.
 *
 * `partial` is the state this exists for: the step's own model is unreachable
 * but its fallback is not, so verifications keep finishing on a weaker model
 * than the golden set measured — which looks like "working" from every other
 * screen.
 */
export interface SeatHealth {
  agent: string
  state: 'up' | 'partial' | 'down'
  primary: ModelHealth
  fallbacks: ModelHealth[]
}

/**
 * A set of durations as the conferência-time report sends them, in
 * milliseconds. Absent statistics mean there was nothing to measure, never zero.
 */
export interface DurationSummary {
  count: number
  mean?: number
  median?: number
  p75?: number
  p90?: number
  total: number
}

/** Wall clock, business hours and the same-day sample, as `@rp/core` computes them. */
export interface IntervalSummary {
  wallClock: DurationSummary
  business: DurationSummary
  sameDay: DurationSummary
  endedOutsideHours: number
}

export interface BenchSummary extends IntervalSummary {
  entered: number
  measured: number
  open: number
  bypassed: number
  returned: number
}

export interface VerifierSummary extends IntervalSummary {
  ready: number
  verified: number
}

/** `unavailable` is ours: no ERP or no integrated filial, so nothing to read. */
export type BenchState = 'running' | 'ready' | 'failed' | 'unavailable'

export interface ConferenceTimeReport {
  window: { from: string; to: string }
  /** The positions the bench was read as, the account's own (#673). */
  stagePositions: StagePositions
  calendar: { weekday: string; saturday: string; sunday: string; holidays: string[] }
  bench: {
    state: BenchState
    chunksDone?: number
    chunksTotal?: number
    error?: string
    collectedAt?: string
    all?: BenchSummary
    withReading?: BenchSummary
    withoutReading?: BenchSummary
  }
  verifier: VerifierSummary
}

/** What the screen asks for: a window of local days and the pharmacy's hours. */
export interface ConferenceTimeQuery {
  from: string
  to: string
  weekday: string
  saturday: string
  sunday: string
  holidays: string
  /** Whose: a super admin may name any account. Absent, the session's (#640). */
  account?: string | undefined
  refresh?: boolean
}

/**
 * The findings report (#551), as `summariseFindings` in `@rp/core` counts it.
 *
 * Mirrored rather than imported, like the rest of this file: the keys of
 * `outcome` are `ReportBasis`, of an audit tally `AuditStanding`, of
 * `corrections` `CorrectionStanding`, and `path` is `ReviewPath`.
 */
export interface FindingTally {
  flagged: number
  worked: number
  corrected: number
  notAnIssue: number
  answeredOpen: number
  unanswered: number
}

export type ReportBasis = 'answered' | 'audited'
export type AuditStanding = 'agreed' | 'disagreed' | 'pending' | 'unclassifiable'
export type ReviewPath = 'unreviewed' | 'correction_pending' | 'reviewed'
export type CorrectionStanding = 'resolved' | 'waiting' | 'returned' | 'released_owing' | 'closed'
export type AuditTally = Record<AuditStanding, number>

export interface FindingOutcome {
  confirmed: number
  falsePositives: number
  falseNegatives: number
}

export interface FindingCohortRow {
  verifications: number
  withFindings: number
  tally: FindingTally
}

export interface FindingCheckRow {
  check: string | null
  rule: string | null
  ruleTitle?: string
  tally: FindingTally
  misses: number
  outcome: Record<ReportBasis, FindingOutcome>
  responses: ResponseTally
}

export interface ResponseTally {
  total: number
  responded: number
  agreed: number
  disputed: number
  unanswered: number
  confirmed: number
  errors: number
  pendingAudit: number
  pendingConclusion: number
  awaitingTriage: number
  unreviewedAgreed: number
}

export interface FindingsEngagement {
  conferencesWithFindings: number
  conferencesWithResponses: number
  closedWithFindings: number
  closedWithUnanswered: number
  closedWithoutResponses: number
  closedUnanswered: number
  producedWithFindings: number
  producedWithKnownConclusion: number
  producedWithoutConclusion: number
}

/** As recorded at first eligible production; unknown history stays outside rate denominators. */
export interface FindingsProductionUsage {
  produced: number
  withFindingsKnown: number
  withoutFindingsKnown: number
  findingsUnknown: number
  responsesKnown: number
  withoutResponses: number
  responsesUnknown: number
  conclusionKnown: number
  withoutConclusion: number
  conclusionUnknown: number
  /** Of everything produced: nobody of the pharmacy opened it before it left. */
  openingKnown: number
  withoutOpening: number
  openingUnknown: number
}

export interface FindingsAttentionItem {
  id: string
  accountId: string
  accountName: string
  branchCode: number
  quotationNumber: number
  status: string
  flagged: number
  responded: number
  unanswered: number
}

export interface FindingsAttention {
  total: number
  page: number
  pageSize: number
  items: FindingsAttentionItem[]
}

export interface FindingTrendPoint {
  from: string
  verifications: number
  corrected: number
  notAnIssue: number
  open: number
}

export interface FindingsReport {
  verifications: { total: number; analysed: number; withFindings: number }
  findings: FindingTally
  responses: ResponseTally
  engagement: FindingsEngagement
  productionUsage: FindingsProductionUsage
  misses: number
  audit: { corrected: AuditTally; notAnIssue: AuditTally; misses: AuditTally }
  outcome: Record<ReportBasis, FindingOutcome>
  byStatus: Array<FindingCohortRow & { status: string }>
  production: Array<FindingCohortRow & { path: ReviewPath }>
  corrections: { sent: number } & Record<CorrectionStanding, number>
  byCheck: FindingCheckRow[]
  reasons: Array<{ reason: string | null; count: number }>
  byStage: Array<{ stage: string | null; corrected: number; notAnIssue: number }>
  trend: { grain: 'day' | 'week'; points: FindingTrendPoint[] }
}

/** Which conferências and which of their findings a findings read is about. */
export interface FindingsScope {
  from: string
  to: string
  /** Empty is every account. */
  account: string
  /** Empty is every state, as on any filter here. */
  states: readonly FindingState[]
}

/** One number of the per-check table: a row of it, and a column. */
export interface FindingsCell {
  check: string | null
  rule: string | null
  cell: CheckCell
  basis: ReportBasis
}

/** A finding or a miss behind a number of the per-check table (#556). */
export interface FindingListItem {
  id: string
  verificationId: string
  fingerprint: string
  check: string | null
  /** Null for a miss: it was never a finding. */
  state: FindingState | null
  at: string
  message: string | null
  evidence: Record<string, unknown>
  note: string | null
  branchCode: number
  quotationNumber: number
  accountId: string
  accountName: string
}

export interface FindingsList {
  total: number
  page: number
  pageSize: number
  items: FindingListItem[]
}

export interface FindingsAttentionScope {
  from: string
  to: string
  account: string
}

function findingsAttentionParams(scope: FindingsAttentionScope, page: number): URLSearchParams {
  return new URLSearchParams({
    ...(scope.from === '' ? {} : { from: scope.from }),
    ...(scope.to === '' ? {} : { to: scope.to }),
    ...(scope.account === '' ? {} : { account: scope.account }),
    page: String(page),
  })
}

function findingsScopeParams(scope: FindingsScope): URLSearchParams {
  return new URLSearchParams({
    ...(scope.from === '' ? {} : { from: scope.from }),
    ...(scope.to === '' ? {} : { to: scope.to }),
    ...(scope.account === '' ? {} : { account: scope.account }),
    ...(scope.states.length === 0 ? {} : { states: scope.states.join(',') }),
  })
}

export interface SystemStatus {
  dependencies: Array<{ name: string; ok: boolean; ms: number; error?: string }>
  /** Null when nobody could be asked, never an empty list. */
  seats: SeatHealth[] | null
  instance: {
    version: string
    startedAt: string
    uptimeSeconds: number
    memoryMb: number
    pid: number
  }
  dependenciesOk: boolean
}

/** One account's instance of one job, as the jobs screen lists it. */
export interface JobStatus {
  kind: string
  title: string
  description: string
  touchesCustomerDatabase: boolean
  accountId: string
  accountName: string
  accountSlug: string
  registered: boolean
  paused: boolean
  cron?: string
  lastRunAt?: string
  nextRunAt?: string
  /** A run is in flight now. `lastRunAt` only says when one started. */
  running: boolean
  runningSince?: string
  /** Since when the schedule's own pass has been running, if one is. */
  scheduledSince?: string
  catalogSyncedAt?: string
  products: number
}

/** One past execution of a resolution case, as the history lists it. */
export interface ResolutionRunSummary {
  id: string
  ran_by: string
  created_at: string
  score: ResolutionScore
}

/** One past execution, whole — with the expectations it was graded against. */
export interface ResolutionRunDetail extends ResolutionRunSummary {
  expected: ResolutionCaseIngredient[]
  observed: ObservedResolution[]
}

/** A rectangle on a page, normalised 0..1 with the origin top-left. */
export interface LabelRegion {
  page: number
  x: number
  y: number
  w: number
  h: number
}

/** A labelled value and where it sits on the page. */
export interface Labelled<T> {
  value: T
  regions: LabelRegion[]
}

export interface GoldenLabel {
  caseId: string
  writing: string
  pageCount: number
  ocrLowConfidence: boolean
  preparations: Array<{
    form: Labelled<string | null>
    posology: Labelled<string | null>
    quantity: Labelled<string | null>
    ingredients: Array<{
      name: Labelled<string>
      dose: Labelled<string | null>
      /** Not `Labelled`: a judgement about the line, whose words `name` boxes. */
      kind: 'active' | 'excipient'
    }>
  }>
  /**
   * The names as written, boxed like any other field.
   *
   * Judgements once -- 'agree' / 'differ' -- so labels could be tracked in
   * git without carrying patient data. That made both unmeasurable, so the
   * names went in and the labels came out of git.
   */
  patient: Labelled<string | null>
  prescriber: Labelled<string | null>
  /**
   * The prescriber's council registration as written (#277). Absent on a
   * label nobody has labelled it on yet, which is not a page that shows none.
   */
  prescriberRegistration?: Labelled<string | null>
  status: string
  correctedBy: string | null
  correctedAt: string | null
  notes: string
}

/** One filial, as the operator's branches screen shows it. */
export interface Branch {
  id: string
  erpCode: string
  name: string
  city?: string
  state?: string
  /** Whether we verify its requisitions at all (#676). */
  enabled: boolean
  /** Who last switched `enabled`, and when. Absent while nobody has. */
  switchedBy?: string
  switchedAt?: string
  syncedAt?: string
  accountName: string
  /** How many products this branch carries, not how many the account has. */
  products: number
  /** What it is equipped to make. Empty means unconstrained. */
  capabilities: string[]
}

/** One option in the queue's filial filter. */
export interface QueueBranch {
  code: number
  /** The branch's name, or its code when the sync has not brought it across. */
  name: string
}

/** An account this session may be issued for. */
export interface SessionAccount {
  id: string
  name: string
  slug: string
  internal: boolean
}

/** One operational rule, as the settings screen lists it. */
export interface RuleSetting {
  id: string
  title: string
  /** One sentence on what it checks. */
  description: string
  severity: 'error' | 'reminder'
  section: string
  enabled: boolean
  /** How many findings it raised for this account in the window. */
  fired: number
}

/** A customer, as the operator's list shows it. */
export interface AccountSummary {
  id: string
  slug: string
  name: string
  internal: boolean
  catalogSyncedAt?: string
  createdAt: string
  branches: number
  people: number
}

/** Where an account's prescriptions are read from (#639). Mirrors `AttachmentStore` in `@rp/core`. */
export type AttachmentStore = 'erp_database' | 'google_drive'

/**
 * Which of Brazil's zones an account keeps (#674). Mirrors `PharmacyTimeZone`
 * in `@rp/core`, through `TIME_ZONE_LABEL`'s test.
 */
export type PharmacyTimeZone =
  | 'America/Noronha'
  | 'America/Belem'
  | 'America/Fortaleza'
  | 'America/Recife'
  | 'America/Araguaina'
  | 'America/Maceio'
  | 'America/Bahia'
  | 'America/Santarem'
  | 'America/Sao_Paulo'
  | 'America/Campo_Grande'
  | 'America/Cuiaba'
  | 'America/Porto_Velho'
  | 'America/Boa_Vista'
  | 'America/Manaus'
  | 'America/Eirunepe'
  | 'America/Rio_Branco'

/**
 * What saving a zone (#674) or an ERP read window (#675) did to the account's
 * schedules: the jobs it moved, or why it moved none. `null` where the server
 * has no scheduler.
 */
export type ScheduleRealignment = { realigned: string[] } | { error: string } | null

/**
 * When an account's ERP may be read (#675), on its own clock, with what follows
 * from it. `start` and `end` are `HH:MM`, both or neither.
 */
export interface AccountErpReadWindowView {
  start: string | null
  end: string | null
  timeZone: PharmacyTimeZone
  /** Whether the periodic reads may dial the ERP at this moment. */
  readableNow: boolean
  /** When the full catalogue sweep runs, `HH:MM` on the account's clock. */
  catalogSweepAt: string
}

/** What the form sends: empty ends read at any hour. */
export interface AccountErpReadWindowForm {
  start: string
  end: string
}

/**
 * An account's ERP connection as the server shows it (#639). Never the
 * password: only whether one is set. `source` mirrors `ErpConnectionSource`
 * in `@rp/core`.
 */
export interface AccountErpView {
  source: 'account' | 'environment' | 'none'
  enabled: boolean
  canSave: boolean
  defaults: {
    port: number
    charset: string
    authPlugin: string
    wireCrypt: boolean
    statementTimeoutMs: number
  }
  connection: null | {
    host: string
    port: number
    database: string
    attachmentsDatabase: string | null
    attachmentsHost: string | null
    user: string
    passwordSet: boolean
    charset: string
    authPlugin: string
    wireCrypt: boolean
    statementTimeoutMs: number
    setBy: string
    setAt: string
  }
}

/** What the form sends. An empty password keeps the stored one. */
export interface AccountErpForm {
  host: string
  port: number
  database: string
  /** Empty when the prescriptions are not in the ERP. */
  attachmentsDatabase: string
  attachmentsHost: string
  user: string
  password: string
  charset: string
  authPlugin: string
  wireCrypt: boolean
  statementTimeoutMs: number
}

/** Where an account's prescriptions are read from, as the server shows it (#639). */
export interface AccountAttachmentStoreView {
  store: AttachmentStore
  driveFolderId: string | null
  /** The e-mail to share a Drive folder with; null when this server has no Google key. */
  driveServiceAccount: string | null
  setBy: string | null
  setAt: string | null
}

export interface AccountAttachmentStoreForm {
  store: AttachmentStore
  /** The folder's link or its id; the server keeps the id. */
  driveFolderId: string
}

/** A bench stage's wire value. Mirrors `QueueStage` in `@rp/core`. */
export type QueueStageValue = 'comercial' | 'conferencia' | 'producao'

/**
 * Where each stage starts on an account's own FC12540 (#673), one position
 * per stage. Mirrors `StagePositions` in `@rp/core`.
 */
export type StagePositions = Readonly<Record<QueueStageValue, number>>

/** An account's stage positions as its page shows them (#673). */
export interface AccountStagePositionsView {
  positions: StagePositions
  /** What an account with none of its own reads. */
  defaults: StagePositions
  setBy: string | null
  setAt: string | null
}

/** One customer, with the one thing configured per customer: which rules run. */
export interface AccountDetail extends AccountSummary {
  rules: RuleSetting[]
  /** The window `RuleSetting.fired` counts over, in days. */
  ruleFiredWindowDays: number
  /** Quotations are verified as soon as they are keyed, before approval (#437). */
  earlyQuotationCapture: boolean
}

/**
 * How one account compares pharmaceutical forms (#607). Mirrors the forms
 * route; the form ids and condition ids are `@rp/core`'s, sent with their
 * labels so the screen never spells them itself.
 */
export interface FormSettings {
  /** Every form, with the built-in names no account can change. */
  forms: Array<{ id: string; label: string; builtInNames: string[] }>
  conditions: Array<{ id: string; label: string }>
  synonyms: FormSynonym[]
  equivalences: FormEquivalence[]
}

/** Mirrors `FormSynonymStatus` in `@rp/core`. */
export type FormSynonymStatus = 'pending' | 'approved' | 'rejected'

export interface FormSynonym {
  id: string
  name: string
  form: string
  status: FormSynonymStatus
  proposedBy: string
  proposedFrom?: string
  proposedAt: string
}

export interface FormEquivalence {
  prescribed: string
  keyed: string
  when?: string
}

/**
 * What a capsule shell is described by (#608): its axes and traits, ids from
 * `@rp/core`, sent with their labels by the catalogue routes.
 */
export interface CapsuleOptions {
  axes: Array<{ id: string; label: string }>
  traits: Array<{ id: string; label: string; axis: string }>
}

/** A product as a capsule shell (#636). */
export interface ProductShell {
  /** Its name starts like a shell, so a verification reads it as one. */
  byName: boolean
  /** What its name is read as: what every verification uses until corrected. */
  read: string[]
  /** What the account said it is, which wins; null when nobody did. */
  corrected: { traits: string[]; by: string; at: string } | null
}

/**
 * What an excipient can be indicated for (#617). The ids are `@rp/core`'s
 * `ExcipientRequirement`, sent with the screen's label by the catalogue routes.
 */
export interface ExcipientRequirementOption {
  id: string
  label: string
}

/** What the account says a product is the excipient for, and who said it. */
export interface ExcipientAnswers {
  answers: string[]
  by: string
  at: string
}

/** A branch and what it is equipped to make. Empty means unconstrained. */

/** An account an invite may name. Super admins only. */
export interface InvitableAccount {
  id: string
  name: string
  internal: boolean
}

/**
 * A role: a named set of permissions, one account's own (#280). The
 * permission ids are `Permission` in @rp/core.
 */
export interface AccessRole {
  id: string
  name: string
  permissions: string[]
  /** The system role (ADR 0024): every permission, never edited or deleted. */
  system: boolean
  /** How many active members hold it. */
  members: number
}

/** A member of this account, with their role and Formula Certa user. */
/** One pharmacy a person belongs to, with their role and Formula Certa user there. */
export interface Membership {
  account_id: string
  account_name: string
  role_id: string | null
  role_name: string | null
  erp_user: string | null
}

/**
 * A person, with the pharmacies they belong to that this session may see:
 * every one for a super admin, the session's own for anybody else.
 */
export interface AccountMember {
  user_id: string
  email: string
  name: string | null
  /** pharmacist or super_admin; a super admin sees everything whatever the role. */
  system_role: string
  active: boolean
  memberships: Membership[]
}

/** A role as the screens offer it, with the pharmacy it belongs to. */
export interface RoleOption {
  id: string
  name: string
  account_id: string
  /** The system role (ADR 0024). */
  system?: boolean
}

export interface PendingInvite {
  id: string
  email: string
  role: string
  /** The role they start with here; null for a super admin. */
  role_name: string | null
  /** The pharmacy they were invited into. */
  account_name: string
  expires_at: string
  created_at: string
}

export interface SynonymCandidate {
  id: string
  erpCode: string
  productName: string
  term: string
  source: 'model' | 'pharmacist'
  /** How many times proposed. Orders the queue; never promotes. */
  occurrences: number
  createdAt: string
}

export interface DraftIngredient {
  name: string
  dose: string | null
  erpCode: string
}

export interface DraftPreparation {
  form: string | null
  posology: string | null
  quantity: string | null
  ingredients: DraftIngredient[]
}

/**
 * What the operational rules read, for a simulation to set by hand.
 *
 * None of it can be read off a prescription image -- whether the substance is
 * controlled, when the prescription was written, what the customer asked for --
 * so without it every rule is unreachable in a simulation. Absent means unknown,
 * exactly as when the ERP does not record it.
 */
export interface DraftRuleScenario {
  compoundingBranchCode?: string | null
  prescribedOn?: string | null
  treatmentDays?: number | null
  animalWeightKg?: number | null
  patientNotes?: string | null
  capsuleKind?: string | null
  modifiedRelease?: boolean | null
  veterinary?: boolean | null
  physician?: boolean | null
}

export interface DraftQuotation {
  patientName: string | null
  prescriberName: string | null
  /** The prescriber's council registration as keyed (#277). */
  prescriberRegistration?: string | null
  preparations: DraftPreparation[]
  rules?: DraftRuleScenario
}

/** One past simulation. Snake case because these come straight from SQL. */
/** How a reading compared to its golden-set label. Mirrors @rp/scoring. */
export interface LabelScore {
  ingredients: { expected: number; found: number; correct: number; f1: number }
  doses: { expected: number; found: number; correct: number; f1: number }
  grouping: { expected: number; actual: number; ok: boolean }
  form: { expected: number; found: number; correct: number; f1: number }
  quantity: { expected: number; found: number; correct: number; f1: number }
  patient: string
  prescriber: string
  overall: number
  rows: ScoreRow[]
}

/** One field compared, exactly as the score compared it. */
export interface ScoreRow {
  preparation?: number
  label: string
  expected: string | null
  actual: string | null
  match: string
  counted: boolean
}

export interface GoldenCaseSource {
  caseId: string
  score?: LabelScore
  scoreSkipped?: string
  /** The label has not been confirmed yet, so the number is provisional. */
  scoreIsDraft?: boolean
}

export interface SimulationRun {
  uploadId: string
  /** Null until the run is dispatched, so a working run still lists. */
  verificationId: string | null
  verificationStatus: string | null
  status: string
  failureReason: string | null
  createdAt: string
  score?: LabelScore
}

export interface SimulationRow {
  id: string
  status: string
  created_at: string
  /** Null when the verification was made before the upload row, or the upload is gone. */
  filename: string | null
  uploaded_by: string | null
  quotation_number: number
  /** The conferência a copy was made from (#586). Null for an upload. */
  source_branch_code: number | null
  source_quotation_number: number | null
  copy_basis: SimulationCopyBasis | null
}

/** One seat: what the registry says, and what is actually running. */
export interface AiSeat {
  readonly agent: string
  /** `registry.ts`'s answer. Changing this is a reviewed diff. */
  readonly registryModel: string
  /** What runs. The same, unless somebody intervened. */
  readonly model: string
  /**
   * What OpenRouter tries if this model errors, in order.
   *
   * Shown, never edited: a fallback is a model choice, and model choices are a
   * diff to `registry.ts`. What it tells somebody reading the screen is that
   * this step keeps working when a provider does not.
   */
  readonly fallbacks: readonly string[]
  /** What the registry's own chain is, whatever the override says. */
  readonly registryFallbacks: readonly string[]
  readonly override: {
    readonly model: string | null
    readonly fallbacks: readonly string[] | null
    readonly reason: string
    readonly setBy: string
    readonly setAt: string
  } | null
}

/** One model's usage over the window. */
export interface AiSpendRow {
  readonly model: string
  /** The step. Null for model calls made outside one. */
  readonly agent: string | null
  readonly calls: number
  readonly tokens: number
  readonly cost: number
}

/** One account's model spend on one (UTC) day. `accountId` null: calls that carried none. */
export interface AiAccountDaySpend {
  readonly day: string
  readonly accountId: string | null
  readonly calls: number
  readonly tokens: number
  readonly cost: number
}

/** What each pharmacy cost, day by day (#640). Null spend: nobody is collecting. */
export interface AiAccountSpend {
  readonly days: number
  readonly accounts: ReadonlyArray<{ readonly id: string; readonly name: string }>
  readonly spend: readonly AiAccountDaySpend[] | null
  readonly spendAvailable: boolean
}

export interface AiManagement {
  readonly days: number
  readonly seats: readonly AiSeat[]
  /**
   * Null when nobody is collecting, which is not the same as nothing spent.
   * The screen says which, because a confident zero over a dead exporter is
   * how Langfuse ingestion stayed suspended for days without anyone noticing.
   */
  readonly spend: readonly AiSpendRow[] | null
  readonly spendAvailable: boolean
}

export const api = {
  requestCode: (email: string) =>
    request<{ sent: boolean }>('/api/auth/request-code', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  verifyCode: (email: string, code: string) =>
    request<{ token: string; user: { email: string; role: string } }>('/api/auth/verify-code', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    }),

  queue: (params: {
    status?: string
    /** 'yes' or 'no' -- whether the verification has anything needing a person. */
    flagged?: string
    /** Which bench stage. Absent means the default, which hides produção. */
    stage?: string
    branch?: string
    q?: string
    sort?: string
    dir?: string
    /** 'asc' or 'desc' puts the status ahead of `sort`; absent, the column sorts alone. */
    status_dir?: string
    /** 'corrections' for the corrections list; absent, the verification queue (#439). */
    view?: string
    /** A window of days on one of the ERP's dates: 'keyed_at' or 'pickup_at'. */
    date?: string
    from?: string
    to?: string
    limit?: number
    offset?: number
  }) => {
    const search = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') search.set(key, String(value))
    }
    return request<{
      /** Every filial the account has, so narrowing does not eat the options. */
      /** Filial options: the code filters, the name labels. */
      branches?: QueueBranch[]
      verifications: QueueRow[]
      total: number
      counts: QueueCounts
      /** What the search decoded to when it was a scanned barcode. */
      scanned?: { branchCode: number; quotationNumber: number } | null
      limit: number
      offset: number
    }>(`/api/verifications?${search.toString()}`)
  },

  verification: (id: string) => request<VerificationDetail>(`/api/verifications/${id}`),

  /**
   * Where the analysis has got to, for the progress strip.
   *
   * Separate from `verification` because it is the one thing worth asking for
   * repeatedly: the detail carries the prescription, the quotation, every
   * finding and a workflow query, which is a great deal of work to learn that
   * the checkpoint has not moved yet.
   */
  progress: (id: string) => request<VerificationProgress>(`/api/verifications/${id}/progress`),

  /**
   * The uuid behind the two numbers a pharmacy says.
   *
   * A second key to the same row, so an address can be `/6/833127` instead of
   * a uuid nobody can read out. Returns the id alone; the screen then loads the
   * verification the way it always has.
   */
  verificationIdByNumber: (branch: number, quotation: number) =>
    request<{ id: string }>(`/api/verifications/by-number/${branch}/${quotation}`),

  /**
   * Something to say about the conferência itself.
   *
   * Not attached to a finding, and so available when there are none -- the
   * case that had no voice at all, including "a receita está ilegível", which
   * is the one comment that makes the whole comparison worthless.
   */
  /**
   * Pulls in an orçamento discovery has not brought.
   *
   * Discovery reads forward from a watermark, so one it has already passed can
   * never arrive on its own. The ERP is asked first: a number nobody has an
   * orçamento for comes back 404, rather than becoming a verification that
   * exists, fails, and sits in the queue looking real.
   */
  /**
   * Fetches one orçamento from the ERP and starts its conferência.
   *
   * Without a filial the server finds it; when the number is at more than one
   * it refuses with a `RequestError` whose body lists them (409).
   */
  syncQuotation: (quotationNumber: number, branchCode?: number) =>
    request<{ verificationId: string; created: boolean; branchCode?: number }>(
      `/api/verifications/sync`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          branchCode === undefined ? { quotationNumber } : { branchCode, quotationNumber },
        ),
      },
    ),

  /**
   * Adds a comment, provisionally.
   *
   * It waits with the decisions and is carried into the histórico when the
   * conferência is concluded -- so until then it can be taken back. On one
   * already concluded there is no conclusion coming, and it goes straight to
   * the trail: `committed` says which happened.
   */
  addComment: (id: string, text: string) =>
    request<{ comment?: VerificationComment; committed: boolean }>(
      `/api/verifications/${id}/comments`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      },
    ),

  /** Takes one back, while the conferência is still open. */
  removeComment: (id: string, commentId: string) =>
    request<{ ok: true }>(`/api/verifications/${id}/comments/${commentId}`, { method: 'DELETE' }),

  recordDecision: (
    id: string,
    fingerprint: string,
    decision: string,
    options?: {
      notes?: string | undefined
      synonymTerm?: string | undefined
      /**
       * Why the pharmacist disagreed.
       *
       * Replaces the `misread` boolean, which was one of four causes and the
       * only one anybody could record. The server still keeps `misread` in
       * step, because it is what the misreading count reads.
       */
      reason?: string | undefined
    },
  ) =>
    request<{ recorded: boolean; synonym?: string }>(
      `/api/verifications/${id}/findings/${fingerprint}/decision`,
      {
        method: 'POST',
        body: JSON.stringify({
          decision,
          notes: options?.notes,
          synonymTerm: options?.synonymTerm,
          reason: options?.reason,
          // Only ever true alongside "não é divergência": it is a statusment
          // about our reading, not about the quotation.
        }),
      },
    ),

  /** What this person set the last time they worked, from their account. */
  preferences: () => request<{ preferences: Record<string, unknown> }>('/api/preferences'),

  /** Merged into whatever else is stored, so one screen cannot erase another's. */
  savePreference: (key: string, value: unknown) =>
    request<{ saved: boolean }>(`/api/preferences/${key}`, {
      method: 'PUT',
      body: JSON.stringify(value),
    }),

  /**
   * Each preparation's rótulo, read from the ERP now: for a conferência read
   * before its rótulo was written (Galdino, 2026-09-29).
   */
  liveLabels: (id: string) =>
    request<{
      labels: Array<{ preparationId: string; label: { lines?: string[] } | null }>
    }>(`/api/verifications/${id}/labels`),

  /** The ficha de produção, read from the ERP now: for a conferência read before it was stored. */
  liveProductionSheet: (id: string) =>
    request<{ sheet: unknown }>(`/api/verifications/${id}/production-sheet`),

  /** Everything that happened to this conferência, oldest first. */
  conferenceHistory: (id: string) =>
    request<{ events: ConferenceEvent[]; statuses?: StatusEntry[] }>(
      `/api/verifications/${id}/history`,
    ),

  /**
   * Takes the conferência, or learns who already has it.
   *
   * Answering with the holder rather than an error is deliberate: somebody
   * else working on it is a normal situation, and a 4xx would make the screen
   * treat it as a fault.
   */
  /** The conferência the queue would offer next, or null. */
  /** The next to work: waiting on the pharmacist, or on a correction (#439). */
  nextVerification: (after: string, status?: 'awaiting_correction') =>
    request<{ id: string | null }>(
      `/api/verifications/next?${new URLSearchParams({
        after,
        ...(status === undefined ? {} : { status }),
      }).toString()}`,
    ),

  claimConference: (id: string) =>
    request<{ holder: string | null; mine: boolean }>(`/api/verifications/${id}/claim`, {
      method: 'POST',
    }),

  /** Gives it back, so the next person does not wait out the lease. */
  releaseConference: (id: string) =>
    request<{ released: boolean }>(`/api/verifications/${id}/claim`, { method: 'DELETE' }),

  /**
   * Declares the conferência finished.
   *
   * What that becomes is the workflow's call: `awaiting_correction` while anything is
   * marked to be corrected, `verified` otherwise.
   */
  finishConference: (id: string) =>
    request<{ finished: 'signalled' | 'gone' }>(`/api/verifications/${id}/finish`, {
      method: 'POST',
    }),

  /** Withdraws a decision, putting the finding back in front of a pharmacist. */
  clearDecision: (id: string, fingerprint: string) =>
    request<{ cleared: boolean }>(`/api/verifications/${id}/findings/${fingerprint}/decision`, {
      method: 'DELETE',
    }),

  product: (erpCode: string) =>
    request<{
      product: Product
      excipientRequirements: ExcipientRequirementOption[]
      capsule: CapsuleOptions
    }>(`/api/synonyms/products/${encodeURIComponent(erpCode)}`),

  reverify: (id: string) =>
    request<{ requested: boolean }>(`/api/verifications/${id}/reverify`, { method: 'POST' }),

  /** Says a person has it on screen; from the first time, it is theirs (#558). */
  markOpened: (id: string) =>
    request<{ ok: true }>(`/api/verifications/${id}/opened`, { method: 'POST' }),

  /**
   * What the ERP wrote after it was read: a pote (#558), labels that change a
   * posology nobody answered (#614), and a quotation read before approval
   * that was approved or changed since (#437). One answer, asked on one beat.
   */
  lateErp: (id: string) =>
    request<{
      pote: { arrived: boolean; seenAt: string | null }
      label: { pending: string[] }
      quotation?: { approved: boolean; changed: boolean; changedAt: string | null }
    }>(`/api/verifications/${id}/late-erp`),

  /**
   * Compares the posologies again with the labels printed late (#614).
   * `running` when it outlived the request; the late-ERP beat sees the end.
   */
  rejudgePosology: (id: string) =>
    request<{ status: 'done'; replaced: number } | { status: 'running' }>(
      `/api/verifications/${id}/rejudge-posology`,
      { method: 'POST' },
    ),

  /**
   * Overrides the ERP's type for the product a keyed ingredient was keyed
   * with: an excipient, for this account from now on. The server reads the
   * product from the stored quotation (ADR 0014).
   */
  markExcipient: (id: string, ingredientId: string) =>
    request<{ marked: boolean; erpCode: string; productName: string }>(
      `/api/verifications/${id}/ingredients/${encodeURIComponent(ingredientId)}/excipient`,
      { method: 'POST' },
    ),

  /** The same override, set from the product page. */
  markExcipientProduct: (erpCode: string) =>
    request<{ marked: boolean; erpCode: string; productName: string }>(
      `/api/excipients/${encodeURIComponent(erpCode)}`,
      { method: 'POST' },
    ),

  /** Takes the override back, so the ERP's type stands again. */
  unmarkExcipient: (erpCode: string) =>
    request<{ removed: boolean }>(`/api/excipients/${encodeURIComponent(erpCode)}`, {
      method: 'DELETE',
    }),

  /**
   * Stops a workflow that cannot make progress, and queues the verification again.
   *
   * Super admins only, and for one failure: a deployed change a running
   * history cannot replay wedges the execution, and from the queue that is
   * indistinguishable from an verification that is merely slow. `reverify` cannot help
   * -- it is a signal, and a wedged workflow processes none.
   */
  abandon: (id: string) =>
    request<{ abandoned: boolean; workflow: string }>(`/api/verifications/${id}/abandon`, {
      method: 'POST',
    }),

  /**
   * Copies a conferência into a simulation in the internal account, analysed
   * against what the ERP answered when the original was (#586). Super admins
   * only; the simulation is in another account, so the answer names it.
   */
  copyToSimulation: (verificationId: string) =>
    request<SimulationCopyMade>('/api/simulations/copies', {
      method: 'POST',
      body: JSON.stringify({ verificationId }),
    }),

  /**
   * Empties an account's verifications and rewinds discovery so they come back.
   *
   * Temporary, and meant to be deleted: it exists while the environment is
   * being brought up against a real ERP and the same quotations have to be
   * re-verified after every fix. `since` has no default on purpose — without it
   * discovery never re-offers them and the queue stays empty for ever.
   */
  purgeVerifications: (accountSlug: string, since: string, keep: readonly number[] = []) =>
    request<{
      account: string
      deleted: number
      terminated: number
      running: number
      terminationsCapped: boolean
      rediscoverFrom: string
    }>('/api/maintenance/verifications/purge', {
      method: 'POST',
      body: JSON.stringify({ accountSlug, since, keep }),
    }),

  /** Verifications one quotation by number, pre-admitted. 409 carries the existing verification's id. */
  fetchQuotation: (accountSlug: string, branchCode: number, quotationNumber: number) =>
    request<{ verificationId: string }>('/api/maintenance/verifications/fetch', {
      method: 'POST',
      body: JSON.stringify({ accountSlug, branchCode, quotationNumber }),
    }),

  /** Stops and deletes one verification. */
  purgeVerification: (verificationId: string) =>
    request<{ deleted: string; terminated: boolean }>(
      `/api/maintenance/verifications/${encodeURIComponent(verificationId)}/purge`,
      { method: 'POST' },
    ),

  runComparison: (branchCode: number, quotationNumber: number) =>
    request<{ verificationId: string }>('/api/verifications/run', {
      method: 'POST',
      body: JSON.stringify({ branchCode, quotationNumber }),
    }),

  /**
   * Heartbeat and read back in one call.
   *
   * Never throws: presence is decoration on a screen whose job is showing a
   * prescription, and it must not be the reason that screen fails.
   */
  presence: async (verificationId: string): Promise<string[]> => {
    try {
      const body = await request<{ viewers: string[] }>(`/api/presence/${verificationId}`, {
        method: 'POST',
      })
      return body.viewers
    } catch {
      return []
    }
  },

  /**
   * The pages of a prescription, as URLs the browser fetches directly.
   *
   * The <img> tag cannot carry an Authorization header, so these are read via
   * fetch and turned into blob URLs by the caller. Streaming them through a
   * query parameter token would put a credential in every server log.
   */
  /**
   * The pages an verification has, or `undefined` when that could not be found out.
   *
   * Not an empty list: "no pages" is an answer and "no answer" is not, and
   * folding the second into the first is how a cut-off request came to read
   * as a receita without an image.
   */
  pages: async (
    verificationId: string,
  ): Promise<
    | {
        pages: PageRef[]
        /** True while the verification has not identified its attachments yet. */
        pending: boolean
      }
    | undefined
  > => {
    try {
      const body = await request<{
        pages: PageRef[]
        pending?: boolean
      }>(`/api/images/${verificationId}/pages`)
      return { pages: body.pages, pending: body.pending === true }
    } catch {
      return undefined
    }
  },

  /** The receitas people attached to this conferência on our side (#276). */
  manualAttachments: (verificationId: string) =>
    request<{ attachments: ManualAttachment[] }>(
      `/api/verifications/${verificationId}/attachments`,
    ),

  /**
   * Attaches a receita and starts the analysis over with it.
   *
   * Multipart, for the reason `uploadSimulation` gives: the shared helper
   * would set a JSON content type and the server could not find the parts.
   */
  attachReceita: async (verificationId: string, file: File): Promise<ManualAttachment> => {
    const form = new FormData()
    form.set('file', file)
    const token = storedToken()
    const response = await fetch(`/api/verifications/${verificationId}/attachments`, {
      method: 'POST',
      headers: token === undefined ? {} : { Authorization: `Bearer ${token}` },
      body: form,
    })
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { error?: string }
      throw new Error(body.error ?? `o envio falhou (${response.status})`)
    }
    return ((await response.json()) as { attachment: ManualAttachment }).attachment
  },

  /** Removes a receita somebody attached, and analyses again without it. */
  removeReceita: (verificationId: string, attachmentId: string) =>
    request<{ removed: boolean }>(
      `/api/verifications/${verificationId}/attachments/${encodeURIComponent(attachmentId)}`,
      { method: 'DELETE' },
    ),

  pageBlob: async (
    verificationId: string,
    attachmentId: string,
    pageIndex = 0,
  ): Promise<string | undefined> => {
    const token = storedToken()
    try {
      const response = await fetch(
        `/api/images/${verificationId}/pages/${attachmentId}?pageIndex=${pageIndex}`,
        { headers: token === undefined ? {} : { Authorization: `Bearer ${token}` } },
      )
      if (!response.ok) return undefined
      return URL.createObjectURL(await response.blob())
    } catch {
      return undefined
    }
  },

  /**
   * Uploads a prescription and starts reading it.
   *
   * Not JSON: a multipart body, so the browser sets its own boundary. The
   * shared request helper always sets a JSON content type, which would make
   * the server unable to find the parts.
   *
   * Returns as soon as the file is stored. The reading takes a minute or two
   * and is followed through `simulationUpload`.
   */
  uploadSimulation: async (file: File, goldenCaseId?: string): Promise<{ id: string }> => {
    const form = new FormData()
    form.set('file', file)
    if (goldenCaseId !== undefined) form.set('goldenCaseId', goldenCaseId)
    const token = storedToken()
    const response = await fetch('/api/simulations/upload', {
      method: 'POST',
      headers: token === undefined ? {} : { Authorization: `Bearer ${token}` },
      body: form,
    })
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { error?: string }
      throw new Error(body.error ?? `o envio falhou (${response.status})`)
    }
    return (await response.json()) as { id: string }
  },

  /** Every simulation run from one golden-set case, newest first. */
  simulationsForCase: (caseId: string) =>
    request<{ runs: SimulationRun[]; labelIsDraft: boolean }>(
      `/api/simulations/for-case/${encodeURIComponent(caseId)}`,
    ),

  /** How the reading is going, and what it produced. */
  simulationUpload: (id: string) =>
    request<{
      id: string
      status: string
      failure_reason: string | null
      derived_quotation: DraftQuotation | null
      golden_case_id: string | null
    }>(`/api/simulations/${id}`),

  /**
   * The uploaded prescription's bytes, as a blob URL.
   *
   * The route is behind `requireSession`, an <img> tag cannot carry an
   * Authorization header, and a token in the URL would be a credential in
   * every server log.
   */
  goldenSet: () => request<{ cases: GoldenCase[] }>('/api/golden-set'),

  jobs: () => request<{ jobs: JobStatus[]; available: boolean }>('/api/jobs'),

  /** Probes every dependency now. Reaches the pharmacy's database. */
  status: () => request<SystemStatus>('/api/status'),

  /**
   * How long conferência takes, for the account being viewed. Super admin only.
   *
   * The bench half is read from the ERP in the background: the first call
   * starts it and answers `running`, and the screen asks again until it is not.
   */
  conferenceTime: (q: ConferenceTimeQuery) => {
    const params = new URLSearchParams({
      from: q.from,
      to: q.to,
      weekday: q.weekday,
      saturday: q.saturday,
      sunday: q.sunday,
      holidays: q.holidays,
    })
    if (q.account !== undefined && q.account !== '') params.set('account', q.account)
    if (q.refresh === true) params.set('refresh', '1')
    return request<ConferenceTimeReport>(`/api/metrics/conference-time?${params.toString()}`)
  },

  /**
   * How the findings are doing, for the conferências created in a window (#551).
   * Every account unless one is named. Super admin only.
   */
  findingsReport: (scope: FindingsScope) =>
    request<FindingsReport>(`/api/metrics/findings?${findingsScopeParams(scope).toString()}`),

  /** The findings behind one number of the report's per-check table, a page at a time (#556). */
  findingsList: (scope: FindingsScope, cell: FindingsCell, page: number) => {
    const params = findingsScopeParams(scope)
    if (cell.check !== null) params.set('check', cell.check)
    if (cell.rule !== null) params.set('rule', cell.rule)
    params.set('cell', cell.cell)
    params.set('basis', cell.basis)
    params.set('page', String(page))
    return request<FindingsList>(`/api/metrics/findings/list?${params.toString()}`)
  },

  /** Closed or released conferences that still carry findings without a response. */
  findingsAttention: (scope: FindingsAttentionScope, page: number) =>
    request<FindingsAttention>(
      `/api/metrics/findings/attention?${findingsAttentionParams(scope, page).toString()}`,
    ),

  /**
   * What the models are on and what they have cost.
   *
   * Super admin only, and 404s for everyone else -- the screen never renders
   * for a pharmacy's own administrator, and the nav entry never appears.
   */
  ai: (days?: number) =>
    request<AiManagement>(`/api/ai${days === undefined ? '' : `?days=${days}`}`),

  /** What an override may point at. Null when OpenRouter cannot be reached. */
  aiModels: () => request<{ models: string[] | null }>('/api/ai/models'),
  aiAccountSpend: (days: number) => request<AiAccountSpend>(`/api/ai/spend/accounts?days=${days}`),

  /**
   * Points a step at another model, another chain, or both.
   *
   * An absent field means "leave the registry's answer alone"; `fallbacks: []`
   * means "no chain at all", which is a different instruction and is why this
   * takes `undefined` rather than defaulting.
   */
  setSeatModel: (
    agent: string,
    change: { model?: string; fallbacks?: readonly string[] },
    reason: string,
  ) =>
    request<{ ok: true }>(`/api/ai/seats/${encodeURIComponent(agent)}`, {
      method: 'PUT',
      body: JSON.stringify({ ...change, reason }),
    }),

  clearSeatModel: (agent: string) =>
    request<{ ok: true }>(`/api/ai/seats/${encodeURIComponent(agent)}`, { method: 'DELETE' }),

  registerJob: (kind: string, accountId: string) =>
    request<{ outcome: string }>(
      `/api/jobs/${encodeURIComponent(kind)}/${encodeURIComponent(accountId)}/register`,
      { method: 'POST' },
    ),

  /** Starts one run now. Reaches the pharmacy's own database. */
  runJob: (kind: string, accountId: string) =>
    request<{ started: boolean }>(
      `/api/jobs/${encodeURIComponent(kind)}/${encodeURIComponent(accountId)}/run`,
      { method: 'POST' },
    ),

  /**
   * Abandons the run holding this job's id, so the next press can start.
   *
   * The id is deterministic so a second press rejoins the run in flight rather
   * than starting a competing sweep. A run that wedges holds it until it
   * closes, which a wedged run never does.
   */
  cancelJob: (kind: string, accountId: string) =>
    request<{ outcome: 'terminated' | 'nothing-running' }>(
      `/api/jobs/${encodeURIComponent(kind)}/${encodeURIComponent(accountId)}/cancel`,
      { method: 'POST' },
    ),

  pauseJob: (kind: string, accountId: string, paused: boolean) =>
    request<{ paused: boolean }>(
      `/api/jobs/${encodeURIComponent(kind)}/${encodeURIComponent(accountId)}/pause`,
      { method: 'POST', body: JSON.stringify({ paused }) },
    ),

  resolutionSet: () => request<{ cases: ResolutionCaseSummary[] }>('/api/resolution-set'),

  resolutionCase: (caseId: string) =>
    request<{ case: ResolutionCaseFull; problems?: string[] }>(
      `/api/resolution-set/${encodeURIComponent(caseId)}`,
    ),

  /** Adds an empty case. The catalogue and ingredients are filled in after. */
  createResolutionCase: (caseId: string) =>
    request<{ created: string }>('/api/resolution-set', {
      method: 'POST',
      body: JSON.stringify({ caseId }),
    }),

  saveResolutionCase: (caseId: string, kase: ResolutionCaseFull) =>
    request<{ saved: string }>(`/api/resolution-set/${encodeURIComponent(caseId)}`, {
      method: 'PUT',
      body: JSON.stringify({ case: kase }),
    }),

  /**
   * Removes a case from the repository and the bucket.
   *
   * All of it or none of it: a bucket that refuses the delete throws, with the
   * permission it needs in the message, and nothing has changed.
   */
  deleteResolutionCase: (caseId: string) =>
    request<{ deleted: string }>(`/api/resolution-set/${encodeURIComponent(caseId)}`, {
      method: 'DELETE',
    }),

  /** This case's past executions, newest first. */
  resolutionRuns: (caseId: string) =>
    request<{ runs: ResolutionRunSummary[] }>(
      `/api/resolution-set/${encodeURIComponent(caseId)}/runs`,
    ),

  /** One past execution, with the expectations it was graded against then. */
  resolutionRun: (caseId: string, runId: string) =>
    request<{ run: ResolutionRunDetail }>(
      `/api/resolution-set/${encodeURIComponent(caseId)}/runs/${encodeURIComponent(runId)}`,
    ),

  /** Runs a case against the real retrieval and the real resolver. Costs money. */
  runResolutionCase: (caseId: string) =>
    request<{ runId: string; caseId: string }>(
      `/api/resolution-set/${encodeURIComponent(caseId)}/run`,
      { method: 'POST' },
    ),

  /**
   * How a resolution run is going.
   *
   * The same shape as the reading set's, and for the same reason: a run that
   * takes tens of seconds cannot be awaited inside one request.
   */
  resolutionRunStatus: (caseId: string, runId: string) =>
    request<{
      run: {
        status: 'running' | 'done' | 'failed'
        score: ResolutionScore | null
        observed: ObservedResolution[] | null
        error: string | null
      }
    }>(`/api/resolution-set/${encodeURIComponent(caseId)}/runs/${encodeURIComponent(runId)}`).then(
      (answer) => answer.run,
    ),

  /** Which account this session is looking at, and which it may look at. */
  session: () =>
    request<{
      accountId: string
      email: string
      role: string
      accounts: SessionAccount[]
      /** The bench stages this person's role reaches (#280). */
      stages: string[]
      /** Whether to offer the users and roles screens, and inviting (ADR 0024). */
      canManageUsers: boolean
      canInvite: boolean
      /** The statuses this person's role reaches (#439), for the status filter. */
      statuses: string[]
      /** Where each stage starts on this account's FC12540 (#673). */
      stagePositions: StagePositions
    }>('/api/session'),

  /** The roles screen: this account's roles (#280, ADR 0024). */
  roles: () => request<{ roles: AccessRole[]; permissions: string[] }>('/api/roles'),

  role: (id: string) =>
    request<{
      role: AccessRole
      members: Array<{ user_id: string; email: string; name: string | null }>
      permissions: string[]
    }>(`/api/roles/${encodeURIComponent(id)}`),

  /** Super admins only. */
  createRole: (role: { name: string; permissions: string[] }) =>
    request<{ role: AccessRole }>('/api/roles', {
      method: 'POST',
      body: JSON.stringify(role),
    }),

  /** Renames a role and/or replaces its permissions. */
  updateRole: (id: string, change: { name?: string; permissions?: string[] }) =>
    request<{ role: AccessRole }>(`/api/roles/${encodeURIComponent(id)}`, {
      method: 'POST',
      body: JSON.stringify(change),
    }),

  /** Super admins only; refused while anybody holds it. */
  deleteRole: (id: string) =>
    request<{ deleted: string }>(`/api/roles/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /** The users screen: members, pending invites, and whether this session invites. */
  users: () =>
    request<{
      users: AccountMember[]
      invites: PendingInvite[]
      /** Every pharmacy, for a super admin's filter; empty for anybody else. */
      accounts: InvitableAccount[]
      canInvite: boolean
    }>('/api/users'),

  user: (id: string) =>
    request<{
      user: AccountMember
      roles: RoleOption[]
      accounts: InvitableAccount[]
      /** The session's own person, who may not take the system role off themselves. */
      isSelf: boolean
    }>(`/api/users/${encodeURIComponent(id)}`),

  /**
   * Gives a member a role and/or a Formula Certa user in one pharmacy; null
   * clears either. The session's pharmacy unless a super admin names another.
   */
  updateUser: (
    id: string,
    change: { accountId?: string; roleId?: string | null; erpUser?: string | null },
  ) =>
    request<{ updated: boolean }>(`/api/users/${encodeURIComponent(id)}`, {
      method: 'POST',
      body: JSON.stringify(change),
    }),

  /** Adds a person to a pharmacy with a role, at once (super admins). */
  addMembership: (id: string, accountId: string, roleId: string) =>
    request<{ added: boolean }>(`/api/users/${encodeURIComponent(id)}/accounts`, {
      method: 'POST',
      body: JSON.stringify({ accountId, roleId }),
    }),

  /** Takes a person out of a pharmacy (super admins); never the last one. */
  removeMembership: (id: string, accountId: string) =>
    request<{ removed: boolean }>(
      `/api/users/${encodeURIComponent(id)}/accounts/${encodeURIComponent(accountId)}`,
      { method: 'DELETE' },
    ),

  /**
   * Points the session at another account.
   *
   * Returns a new token rather than setting a filter: every route scopes by
   * the session's account and none takes one as a parameter, which is what
   * stops a client asking for another pharmacy's data.
   */
  switchAccount: (accountId: string) =>
    request<{ token: string; accountId: string }>('/api/session/account', {
      method: 'POST',
      body: JSON.stringify({ accountId }),
    }),

  /** The operational rules and each branch's capabilities. */
  /** Every customer. Super admin only. */
  accounts: () => request<{ accounts: AccountSummary[] }>('/api/accounts'),

  /** One customer, rules included. */
  account: (id: string) =>
    request<{ account: AccountDetail }>(`/api/accounts/${encodeURIComponent(id)}`),

  /** The editable part of a customer: name, whether it is ours, which rules are off. */
  updateAccount: (
    id: string,
    patch: { name?: string; disabledRules?: string[]; earlyQuotationCapture?: boolean },
  ) =>
    request<{ account: AccountDetail }>(`/api/accounts/${encodeURIComponent(id)}`, {
      method: 'POST',
      body: JSON.stringify(patch),
    }),

  /** How the account reaches its ERP (#639). Super admin only; never the password. */
  accountErp: (id: string) =>
    request<AccountErpView>(`/api/accounts/${encodeURIComponent(id)}/erp`),

  saveAccountErp: (id: string, form: AccountErpForm) =>
    request<AccountErpView>(`/api/accounts/${encodeURIComponent(id)}/erp`, {
      method: 'PUT',
      body: JSON.stringify(form),
    }),

  removeAccountErp: (id: string) =>
    request<AccountErpView>(`/api/accounts/${encodeURIComponent(id)}/erp`, { method: 'DELETE' }),

  /** Tries the form as typed, or what the account uses now when given nothing. */
  testAccountErp: (id: string, form?: AccountErpForm) =>
    request<{ ok: boolean; ms: number; error?: string }>(
      `/api/accounts/${encodeURIComponent(id)}/erp/test`,
      { method: 'POST', body: JSON.stringify(form ?? {}) },
    ),

  /** Where the account's prescriptions are read from (#639). Super admin only. */
  accountAttachmentStore: (id: string) =>
    request<AccountAttachmentStoreView>(`/api/accounts/${encodeURIComponent(id)}/attachment-store`),

  saveAccountAttachmentStore: (id: string, form: AccountAttachmentStoreForm) =>
    request<AccountAttachmentStoreView>(
      `/api/accounts/${encodeURIComponent(id)}/attachment-store`,
      { method: 'PUT', body: JSON.stringify(form) },
    ),

  /** Reaches the store the form names, or the saved one when given nothing. */
  testAccountAttachmentStore: (id: string, form?: AccountAttachmentStoreForm) =>
    request<{ ok: boolean; ms: number; error?: string }>(
      `/api/accounts/${encodeURIComponent(id)}/attachment-store/test`,
      { method: 'POST', body: JSON.stringify(form ?? {}) },
    ),

  /** Where the account's stages start on its FC12540 (#673). Super admin only. */
  accountStagePositions: (id: string) =>
    request<AccountStagePositionsView>(`/api/accounts/${encodeURIComponent(id)}/stage-positions`),

  /** Refused, with the reason, when the positions are out of the stages' order. */
  saveAccountStagePositions: (id: string, positions: StagePositions) =>
    request<AccountStagePositionsView>(`/api/accounts/${encodeURIComponent(id)}/stage-positions`, {
      method: 'PUT',
      body: JSON.stringify(positions),
    }),

  /** The zone the account's ERP is read on and its schedules fire on (#674). Super admin only. */
  accountTimeZone: (id: string) =>
    request<{ timeZone: PharmacyTimeZone }>(`/api/accounts/${encodeURIComponent(id)}/time-zone`),

  /** Saves it, and moves the schedules the account has onto it. */
  saveAccountTimeZone: (id: string, timeZone: PharmacyTimeZone) =>
    request<{ timeZone: PharmacyTimeZone; schedules: ScheduleRealignment }>(
      `/api/accounts/${encodeURIComponent(id)}/time-zone`,
      { method: 'PUT', body: JSON.stringify({ timeZone }) },
    ),

  /** When the account's ERP may be read (#675). Super admin only. */
  accountErpReadWindow: (id: string) =>
    request<AccountErpReadWindowView>(`/api/accounts/${encodeURIComponent(id)}/erp-read-window`),

  /** Saves it, or clears it with empty ends, and moves the account's schedules. */
  saveAccountErpReadWindow: (id: string, form: AccountErpReadWindowForm) =>
    request<AccountErpReadWindowView & { schedules: ScheduleRealignment }>(
      `/api/accounts/${encodeURIComponent(id)}/erp-read-window`,
      { method: 'PUT', body: JSON.stringify(form) },
    ),

  /** Counts the account's ERP codes against our tables (#672). Super admin only; read-only. */
  checkAccountErpCodes: (id: string) =>
    request<ErpCodesCheckResult>(`/api/accounts/${encodeURIComponent(id)}/erp/codes-check`, {
      method: 'POST',
    }),

  /** An account's form names and equivalences (#607). Super admin only. */
  formSettings: (accountId: string) =>
    request<FormSettings>(`/api/accounts/${encodeURIComponent(accountId)}/forms`),

  /** A name approved outright. Refused with the reason when it would contradict another form. */
  addFormSynonym: (accountId: string, name: string, form: string) =>
    request<{ id: string }>(`/api/accounts/${encodeURIComponent(accountId)}/forms/synonyms`, {
      method: 'POST',
      body: JSON.stringify({ name, form }),
    }),

  decideFormSynonym: (accountId: string, id: string, decision: 'approve' | 'reject') =>
    request<{ id: string }>(
      `/api/accounts/${encodeURIComponent(accountId)}/forms/synonyms/${encodeURIComponent(id)}/${decision}`,
      { method: 'POST' },
    ),

  removeFormSynonym: (accountId: string, id: string) =>
    request<{ removed: boolean }>(
      `/api/accounts/${encodeURIComponent(accountId)}/forms/synonyms/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    ),

  /** The whole list, replacing the account's. */
  saveFormEquivalences: (accountId: string, equivalences: readonly FormEquivalence[]) =>
    request<{ equivalences: FormEquivalence[] }>(
      `/api/accounts/${encodeURIComponent(accountId)}/forms/equivalences`,
      { method: 'PUT', body: JSON.stringify({ equivalences }) },
    ),

  /** Says what one product is as a shell, replacing what its name is read as. Super admin only. */
  setShellTraits: (accountId: string, erpCode: string, traits: readonly string[]) =>
    request<{ traits: string[] }>(
      `/api/accounts/${encodeURIComponent(accountId)}/forms/shells/${encodeURIComponent(erpCode)}`,
      { method: 'PUT', body: JSON.stringify({ traits }) },
    ),

  /** Takes the account's word back: read from the name again, or no shell at all. */
  clearShellTraits: (accountId: string, erpCode: string) =>
    request<{ cleared: boolean }>(
      `/api/accounts/${encodeURIComponent(accountId)}/forms/shells/${encodeURIComponent(erpCode)}`,
      { method: 'DELETE' },
    ),

  /** Says which actives one excipient is the right filler for. */
  setExcipientAnswers: (accountId: string, erpCode: string, answers: readonly string[]) =>
    request<{ answers: string[] }>(
      `/api/accounts/${encodeURIComponent(accountId)}/excipients/${encodeURIComponent(erpCode)}`,
      { method: 'PUT', body: JSON.stringify({ answers }) },
    ),

  /** Back to no opinion on the excipient. */
  clearExcipientAnswers: (accountId: string, erpCode: string) =>
    request<{ cleared: boolean }>(
      `/api/accounts/${encodeURIComponent(accountId)}/excipients/${encodeURIComponent(erpCode)}`,
      { method: 'DELETE' },
    ),

  /** One filial. Super admin only. */
  branch: (id: string) => request<{ branch: Branch }>(`/api/branches/${encodeURIComponent(id)}`),

  /** Declares what a filial is equipped to make; empty means unconstrained. */
  setBranchCapabilities: (id: string, capabilities: string[]) =>
    request<{ branch: Branch }>(`/api/branches/${encodeURIComponent(id)}/capabilities`, {
      method: 'POST',
      body: JSON.stringify({ capabilities }),
    }),

  /** Switches whether a branch is verified, recording who and when (#676). */
  setBranchEnabled: (id: string, enabled: boolean) =>
    request<{ branch: Branch }>(`/api/branches/${encodeURIComponent(id)}/enabled`, {
      method: 'POST',
      body: JSON.stringify({ enabled }),
    }),

  /** Every customer's branches. Super admin only. */
  branches: () => request<{ branches: Branch[] }>('/api/branches'),

  /** Acompanhamento de feedbacks, newest first (#405). Super admin only. */
  feedbackItems: (filter: {
    status: TriageStatus
    account?: string
    page?: number
    /** Received within, the pharmacy's days; empty is unbounded. */
    from?: string
    to?: string
  }) =>
    request<{ items: FeedbackItem[]; total: number; page: number; pageSize: number }>(
      `/api/feedback-triage/items?${new URLSearchParams({
        status: filter.status,
        ...(filter.account === undefined ? {} : { account: filter.account }),
        ...(filter.from === undefined || filter.from === '' ? {} : { from: filter.from }),
        ...(filter.to === undefined || filter.to === '' ? {} : { to: filter.to }),
        page: String(filter.page ?? 1),
      }).toString()}`,
    ),

  /** One eligible queue item. */
  feedbackItem: (eventId: string) =>
    request<{ item: FeedbackItem }>(`/api/feedback-triage/items/${encodeURIComponent(eventId)}`),

  feedbackSequence: (
    filters: FeedbackReviewFilters,
    cursor?: { afterAt: string; afterId: string },
    horizon?: string,
  ) => {
    const query = feedbackQuery(filters)
    if (cursor !== undefined) {
      query.set('afterAt', cursor.afterAt)
      query.set('afterId', cursor.afterId)
    }
    if (horizon !== undefined) query.set('horizon', horizon)
    return request<FeedbackSequence>(`/api/feedback-triage/sequence?${query.toString()}`)
  },

  /** Concordo / discordo on one item; classifying again replaces it. */
  triageFeedback: (eventId: string, verdict: string, note: string) =>
    request<{ triage: unknown }>(`/api/feedback-triage/items/${encodeURIComponent(eventId)}`, {
      method: 'PUT',
      body: JSON.stringify({ verdict, note }),
    }),

  /** Back to pending. */
  clearFeedbackTriage: (eventId: string) =>
    request<{ cleared: boolean }>(`/api/feedback-triage/items/${encodeURIComponent(eventId)}`, {
      method: 'DELETE',
    }),

  /** Falsos positivos e negativos, from the judgements, over a window of days. */
  feedbackPanel: (filter: { from: string; to: string; account?: string }) =>
    request<FeedbackPanel>(
      `/api/feedback-triage/panel?${new URLSearchParams({
        from: filter.from,
        to: filter.to,
        ...(filter.account === undefined ? {} : { account: filter.account }),
      }).toString()}`,
    ),

  /** What the invite form may offer this session. */
  inviteForm: () =>
    request<{
      /** Only a super admin gets a list: only they may name an account. */
      accounts: InvitableAccount[]
      /** Every role in every pharmacy this actor may invite into. */
      roles: RoleOption[]
      grantable: string[]
    }>('/api/invites'),

  invite: (invite: { email: string; role: string; accountIds: string[]; roleName?: string }) =>
    request<{ invite: PendingInvite }>('/api/invites', {
      method: 'POST',
      body: JSON.stringify(invite),
    }),

  goldenCase: (caseId: string) =>
    request<{ label: GoldenLabel }>(`/api/golden-set/${encodeURIComponent(caseId)}`),

  /**
   * Reads this case again and scores it against its own label.
   *
   * A look, not a measurement: nothing is stored, and the baseline stays
   * `eval:reader`'s business, which averages several runs because one sample
   * of a model is not a number to keep. `draft` and `detected` both change
   * what the number means, so both come back with it.
   */
  runGoldenCase: (caseId: string) =>
    request<{ runId: string; caseId: string }>(
      `/api/golden-set/${encodeURIComponent(caseId)}/run`,
      { method: 'POST' },
    ),

  /**
   * How a reading run is going.
   *
   * Polled rather than awaited: the run takes tens of seconds, and a request
   * held open that long is closed underneath the browser at ten.
   */
  goldenRun: (caseId: string, runId: string) =>
    request<{
      run: {
        status: 'running' | 'done' | 'failed'
        result: { caseId: string; score: LabelScore; draft: boolean; detected: boolean } | null
        error: string | null
      }
    }>(`/api/golden-set/${encodeURIComponent(caseId)}/runs/${encodeURIComponent(runId)}`).then(
      (answer) => answer.run,
    ),

  /**
   * Removes a reading case: its label, its pages and its place in the index.
   *
   * All of it or none of it. A bucket that refuses to delete an object throws,
   * with the permission it needs in the message, and the case is left whole --
   * a case dropped from the index whose objects survived is one nobody can
   * reach to remove and nobody can add back.
   */
  deleteGoldenCase: (caseId: string) =>
    request<{ deleted: string }>(`/api/golden-set/${encodeURIComponent(caseId)}`, {
      method: 'DELETE',
    }),

  /**
   * Saves a corrected label.
   *
   * Writes the file the screen used to hand back for pasting. The label stays
   * where it was, so a change to what "correct" means is still a diff.
   */
  saveGoldenCase: (caseId: string, label: GoldenLabel) =>
    request<{ saved: boolean }>(`/api/golden-set/${encodeURIComponent(caseId)}`, {
      method: 'PUT',
      body: JSON.stringify(label),
    }),

  /**
   * Whether this conferência is already in each set, before offering to add it.
   *
   * The dialog names two sets and has to say which of them already holds this
   * case -- one somebody may have corrected since, and which adding will not
   * overwrite. Asking is the only way to know without creating the other to
   * find out.
   */
  goldenSetForVerification: (verificationId: string) =>
    request<{
      caseId: string
      hasReading: boolean
      configured: boolean
      reader: boolean
      resolution: boolean
    }>(`/api/golden-set/from-verification/${encodeURIComponent(verificationId)}`),

  /**
   * Adds a conferência to the golden sets chosen, as drafts, under one case id.
   *
   * `sets` says which: the reading case, the resolution case, or both. A set
   * that already holds the case is left exactly as it is -- `exists` rather
   * than `created` -- because the copy there may have been corrected already.
   */
  addVerificationToGoldenSet: (
    verificationId: string,
    sets: readonly GoldenSetName[] = ['reader', 'resolution'],
  ) =>
    request<{
      caseId: string
      created: boolean
      missingPages: number
      reader: GoldenSetOutcome
      resolution: GoldenSetOutcome
    }>(`/api/golden-set/from-verification/${encodeURIComponent(verificationId)}`, {
      method: 'POST',
      body: JSON.stringify({ sets }),
    }),

  /** Adds a case from an uploaded page image. The label starts empty. */
  createGoldenCase: async (
    caseId: string,
    file: File,
  ): Promise<{ created: string; stored: boolean }> => {
    const token = storedToken()
    const form = new FormData()
    form.set('caseId', caseId)
    form.set('file', file)
    const response = await fetch('/api/golden-set', {
      method: 'POST',
      headers: token === undefined ? {} : { Authorization: `Bearer ${token}` },
      body: form,
    })
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { error?: string }
      throw new Error(body.error ?? `falha ao criar (${response.status})`)
    }
    return (await response.json()) as { created: string; stored: boolean }
  },

  /**
   * Runs a golden-set case through the pipeline as a simulation.
   *
   * The case's page is fetched and posted to the ordinary upload, rather than
   * given a route of its own on the server. The image is already reachable
   * and the upload already stores, dispatches and returns an id -- a shortcut
   * between them would be a second way to start a simulation, and the two would
   * drift the first time one of them learned something.
   *
   * The cost is a round trip through the browser for a few megabytes, on a
   * screen one person uses at a time.
   */
  simulateGoldenCase: async (caseId: string): Promise<{ id: string }> => {
    const token = storedToken()
    const page = await fetch(`/api/golden-set/${encodeURIComponent(caseId)}/pages/0`, {
      headers: token === undefined ? {} : { Authorization: `Bearer ${token}` },
    })
    if (!page.ok) {
      throw new Error(
        page.status === 404
          ? 'este caso não tem imagem para simular'
          : `não foi possível ler a imagem (${page.status})`,
      )
    }

    const blob = await page.blob()
    // Named for the case, so the simulation says where it came from in every
    // list that shows a filename.
    const extension = (blob.type.split('/')[1] ?? 'png').replace('jpeg', 'jpg')
    return await api.uploadSimulation(
      new File([blob], `${caseId}.${extension}`, { type: blob.type || 'image/png' }),
      caseId,
    )
  },

  /** Fetched rather than linked: the route is behind the session middleware. */
  goldenPageBlob: async (caseId: string, page: number): Promise<string | undefined> => {
    const token = storedToken()
    try {
      const response = await fetch(`/api/golden-set/${encodeURIComponent(caseId)}/pages/${page}`, {
        headers: token === undefined ? {} : { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) return undefined
      return URL.createObjectURL(await response.blob())
    } catch {
      return undefined
    }
  },

  simulationImageBlob: async (uploadId: string): Promise<string | undefined> => {
    const token = storedToken()
    try {
      const response = await fetch(`/api/simulations/${uploadId}/image`, {
        headers: token === undefined ? {} : { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) return undefined
      return URL.createObjectURL(await response.blob())
    } catch {
      return undefined
    }
  },

  /** Fires the edited quotation as an verification. */
  dispatchSimulation: (id: string, quotation: DraftQuotation) =>
    request<{ verificationId: string }>(`/api/simulations/${id}/dispatch`, {
      method: 'POST',
      body: JSON.stringify({ quotation }),
    }),

  /**
   * Every simulation this account has run, newest first.
   *
   * `id` is the **verification's** id, not the upload's: a dispatched simulation is an
   * verification like any other, excluded from the queue by SQL. This list is the
   * only way back to one, which is why it exists -- before it, dispatching a
   * simulation and then navigating away lost the verification for good.
   *
   * Doubles as the permission probe: 404 for anyone below super admin.
   */
  simulations: async (): Promise<SimulationRow[] | undefined> => {
    try {
      const body = await request<{ simulations: SimulationRow[] }>('/api/simulations')
      return body.simulations
    } catch {
      // 404 for anyone below super admin, which is the same answer as the
      // feature not existing. The nav simply does not appear.
      return undefined
    }
  },

  synonyms: () => request<{ candidates: SynonymCandidate[] }>('/api/synonyms'),

  /**
   * Browsing the catalogue.
   *
   * Not `/search`: that runs the resolver's own retrieval, which is right for
   * picking a product in a simulation and wrong for looking one up.
   */
  catalog: (
    term: string,
    pending: boolean,
    limit = 20,
    offset = 0,
    /** Only the excipients the account described (#617). */
    excipients = false,
    /** Only the capsule shells, by name or marked by the account (#636). */
    shells = false,
  ) =>
    request<{
      products: CatalogProduct[]
      /** Rows matching the search, not the whole catalogue: it drives the pager. */
      total: number
      pendingProducts: number
      limit: number
      offset: number
      excipientRequirements: ExcipientRequirementOption[]
      capsule: CapsuleOptions
    }>(
      `/api/synonyms/catalog?term=${encodeURIComponent(term)}${pending ? '&pending=true' : ''}` +
        `${excipients ? '&excipients=true' : ''}${shells ? '&shells=true' : ''}` +
        `&limit=${limit}&offset=${offset}`,
    ),

  /** Catalogue search, for picking a product by hand in a simulation. */
  searchCatalog: (term: string) =>
    request<{ candidates: CatalogCandidate[] }>(
      `/api/synonyms/search?term=${encodeURIComponent(term)}`,
    ),

  seedDemoCatalog: () =>
    request<{ products: number; terms: number }>('/api/synonyms/demo', { method: 'POST' }),

  /** A synonym added by hand for a product, approved as it lands. */
  addTerm: (erpCode: string, term: string) =>
    request<{ added: boolean }>('/api/synonyms/terms', {
      method: 'POST',
      body: JSON.stringify({ erpCode, term }),
    }),

  /** Takes back a synonym added or approved by hand. */
  removeTerm: (id: string) =>
    request<{ removed: boolean }>(`/api/synonyms/terms/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  approveSynonym: (id: string) =>
    request<{ approved: boolean }>(`/api/synonyms/${id}/approve`, { method: 'POST' }),

  rejectSynonym: (id: string) =>
    request<{ rejected: boolean }>(`/api/synonyms/${id}/reject`, { method: 'POST' }),

  /**
   * The latest adversarial review of a conferência, or none (#541).
   *
   * Polled while a run is going: a review is minutes of model work, and its
   * row is what says how it went.
   */
  adversarialReview: (verificationId: string) =>
    request<{ run: AdversarialRun | null; runs: number }>(
      `/api/adversarial-reviews/${encodeURIComponent(verificationId)}`,
    ),

  /** Starts a run. Answers at once; follow it with `adversarialReview`. */
  startAdversarialReview: (verificationId: string) =>
    request<{ id: string; status: string }>(
      `/api/adversarial-reviews/${encodeURIComponent(verificationId)}`,
      { method: 'POST' },
    ),

  /** Was the review right about this one? `null` withdraws the answer. */
  assessDisagreement: (runId: string, disagreement: string, assessment: ReviewAssessment | null) =>
    request<{ assessments: Record<string, ReviewAssessmentRecord> }>(
      `/api/adversarial-reviews/runs/${encodeURIComponent(runId)}/assessments`,
      { method: 'POST', body: JSON.stringify({ disagreement, assessment }) },
    ),
}
